// Phase C5/C6 — fetches the single wb_company_settings row once at the
// WeighbridgeLayout level and exposes it to every settings sub-page.
//
// Why a context (vs each page calling useQuery directly):
//   • The VAT Settings page (C5) and the Compliance page (C6) read from
//     and write to the SAME server endpoint (PATCH /wb/company-settings).
//   • If both pages independently held a cached copy and one's save
//     emitted a partial PATCH, the other's stale snapshot could silently
//     overwrite fields the operator didn't touch.
//   • Centralising the cache + refetch behind a context keeps a single
//     authoritative copy and forces every save to refresh it.
//
// Each page still owns its OWN partial PATCH — this context only manages
// the GET side. Pages compose `useUpdateWbCompanySettings()` (see
// useWbCompanySettings hook file) for writes.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- DTO ------------------------------------------------------------------
//
// Mirrors WbCompanySettingsDto on the server (see
// WEIGHBRIDGE_SERVER_CONTRACT.md §C6). Note that the server EMBEDS the
// chosen default VAT rate as a nested object on responses but expects an
// enc_id string (`default_vat_rate_id`) on input.
//
// Fields not present on the server (numeric approval thresholds, ticket
// numbering format, ANPR confidence company defaults, advice-note
// template fields) are deliberately omitted from this type — surfacing
// them as form controls would lie about what the server accepts.

export interface WbCompanySettingsDefaultVatRate {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly percentage: number;
}

export interface WbCompanySettingsDto {
  readonly vat_enabled: boolean;
  readonly vat_registration_number: string | null;
  readonly default_vat_rate: WbCompanySettingsDefaultVatRate | null;
  readonly credit_limit_behaviour: 'block' | 'warn' | 'off';
  readonly trade_approved_default: boolean;
  readonly manual_weight_requires_reason_hw_online: boolean;
  readonly manual_weight_requires_reason_hw_offline: boolean;
  readonly stale_tare_threshold_days: number;
  readonly compliance_wtn_enabled: boolean;
  readonly compliance_hazardous_enabled: boolean;
  readonly compliance_carrier_licence_required: boolean;
  readonly compliance_ewc_required_on_waste_lines: boolean;
  readonly stock_alert_email_recipients: string[];
  readonly weight_reconciliation_tolerance_pct: number;
  readonly autosave_draft_interval_sec: number;
  readonly created_at: string;
  readonly updated_at: string;
}

interface WbCompanySettingsContextValue {
  settings: WbCompanySettingsDto | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

const WbCompanySettingsContext =
  createContext<WbCompanySettingsContextValue | null>(null);

export function WbCompanySettingsProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const query = useQuery<{ company_settings: WbCompanySettingsDto }>({
    queryKey: queryKeys.wb.companySettings,
    queryFn: async () => {
      const res = await apiClient.get<{
        company_settings: WbCompanySettingsDto;
      }>(endpoints.wb.companySettings);
      return res.data;
    },
    // 5 minutes — settings change rarely, and any save in this surface
    // invalidates the key explicitly via refetch().
    staleTime: 5 * 60 * 1000,
  });

  const value = useMemo<WbCompanySettingsContextValue>(
    () => ({
      settings: query.data?.company_settings ?? null,
      isLoading: query.isLoading,
      isError: query.isError,
      refetch: () => {
        void query.refetch();
      },
    }),
    [query.data, query.isLoading, query.isError, query.refetch],
  );

  return (
    <WbCompanySettingsContext.Provider value={value}>
      {children}
    </WbCompanySettingsContext.Provider>
  );
}

export function useWbCompanySettings(): WbCompanySettingsContextValue {
  const value = useContext(WbCompanySettingsContext);
  if (value === null) {
    throw new Error(
      'useWbCompanySettings must be used within a WbCompanySettingsProvider',
    );
  }
  return value;
}

/**
 * Autosave pacing hint for the C10 ticket draft flow.
 *
 * Reads `autosave_draft_interval_sec` from the company settings (1–60s,
 * server-validated) and returns the value in milliseconds. Falls back to
 * 10 seconds when the settings haven't loaded yet — matches the server
 * default so the first autosave isn't unreasonably eager or lazy.
 *
 * C10 imports this directly; never compute autosave cadence from anywhere
 * else.
 */
export function useWbAutosaveInterval(): number {
  const { settings } = useWbCompanySettings();
  return (settings?.autosave_draft_interval_sec ?? 10) * 1000;
}
